import { useCallback, useState } from 'react'
import { useWidgetContext } from '../../context/WidgetContext'
import {
  MediaGeneratePanel,
  type MediaGenerateRequest,
} from '../media/MediaGeneratePanel'

const normalizeMediaUrls = (...values: unknown[]): string[] => {
  const merged: string[] = []
  const seen = new Set<string>()

  values.forEach((value) => {
    if (!Array.isArray(value)) return

    value.forEach((raw) => {
      const url = String(raw || '').trim()
      if (!url || seen.has(url)) return
      seen.add(url)
      merged.push(url)
    })
  })

  return merged
}

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

export const ImagePanel = () => {
  const { agent, modeApi, emitOutput } = useWidgetContext()
  const { useGenerateImageMutation, useGetImageStatusMutation } = modeApi as any
  const [generateImage, { isLoading }] = useGenerateImageMutation()
  const [getImageStatus] = useGetImageStatusMutation()

  const [prompt, setPrompt] = useState('')
  const [aspectRatio, setAspectRatio] = useState('auto')
  const [mediaUrls, setMediaUrls] = useState<string[]>([])
  const [generationId, setGenerationId] = useState('')
  const [isPolling, setIsPolling] = useState(false)
  const [error, setError] = useState('')

  const generate = useCallback(
    async (request: MediaGenerateRequest) => {
      const nextPrompt = String(request.prompt || '').trim()
      if (!nextPrompt || isLoading) return
      if (!agent.id) {
        setError('Agent is not ready.')
        return
      }

      setError('')
      setIsPolling(false)
      setAspectRatio(String(request.aspectRatio || 'auto'))

      try {
        const response = await generateImage({
          prompt: nextPrompt,
          agentId: request.agentId,
          aspectRatio:
            request.aspectRatio && request.aspectRatio !== 'auto'
              ? request.aspectRatio
              : undefined,
        }).unwrap()

        const imageId = String((response as any)?.image?.id || '').trim()
        setGenerationId(imageId)
        const initialUrls = normalizeMediaUrls(
          (response as any)?.images,
          (response as any)?.videos,
          (response as any)?.image?.outputUrls
        )

        if (initialUrls.length > 0) {
          setMediaUrls(initialUrls)
          emitOutput({
            type: 'image',
            output: {
              prompt: nextPrompt,
              generationId: imageId || null,
              urls: initialUrls,
              primaryUrl: initialUrls[0] || null,
              aspectRatio: request.aspectRatio,
              source: 'create',
            },
          })
        } else {
          setMediaUrls([])
        }

        const shouldPoll =
          imageId.length > 0 &&
          initialUrls.length === 0 &&
          String((response as any)?.status || '').toLowerCase() !== 'failed'

        if (!shouldPoll) return
        setIsPolling(true)

        for (let attempt = 0; attempt < 25; attempt += 1) {
          await sleep(1500)
          const statusResponse = await getImageStatus({ id: imageId, agentId: request.agentId }).unwrap()
          const statusUrls = normalizeMediaUrls(
            (statusResponse as any)?.images,
            (statusResponse as any)?.videos
          )

          if (statusUrls.length > 0) {
            setMediaUrls(statusUrls)
            emitOutput({
              type: 'image',
              output: {
                prompt: nextPrompt,
                generationId: imageId,
                urls: statusUrls,
                primaryUrl: statusUrls[0] || null,
                aspectRatio: request.aspectRatio,
                source: 'status',
              },
            })
            break
          }

          const status = String((statusResponse as any)?.status || '').toLowerCase()
          if (status === 'failed') {
            setError('Image generation failed.')
            break
          }
        }
      } catch {
        setError('Unable to generate image right now. Please try again.')
      } finally {
        setIsPolling(false)
      }
    },
    [agent.id, emitOutput, generateImage, getImageStatus, isLoading]
  )

  return (
    <div className='osirus-media-panel osirus-media-panel-image'>
      {error && <div className='osirus-generate-error'>{error}</div>}
      <MediaGeneratePanel
        agentId={agent.id}
        prompt={prompt}
        onPromptChange={setPrompt}
        selectedRatio={aspectRatio}
        onRatioChange={setAspectRatio}
        onGenerate={generate}
        isGenerating={isLoading || isPolling}
        generatedUrls={mediaUrls}
        generateLabel='Generate Image'
        generatingLabel={isPolling ? 'Checking status...' : 'Generating...'}
        resultLabel={
          generationId
            ? `Generated Results (${mediaUrls.length || 0})`
            : 'Generated Results'
        }
        onOutputsChange={(urls, agentId) => {
          setMediaUrls(urls)
          emitOutput({
            type: 'image',
            output: {
              prompt,
              generationId: generationId || null,
              urls,
              primaryUrl: urls[urls.length - 1] || urls[0] || null,
              aspectRatio,
              source: 'edit',
              agentId,
            },
          })
        }}
      />
    </div>
  )
}
